import { HeartHandshake, MapPinned, Target } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { PageHero, Reveal, SectionHeading } from "@/components/site/Reveal";
import { usePageMeta } from "@/lib/seo";

const VALUES = [
  { icon: Target, title: "Our mission", text: "Give Queens a serious place to train at a fair price, with hours that fit real life: early shifts, late shifts and everything in between." },
  { icon: HeartHandshake, title: "Beginners welcome", text: "Everybody starts somewhere. No judgment, no attitude. Ask the front desk anything and they'll point you in the right direction." },
  { icon: MapPinned, title: "Built for the neighborhood", text: "From Bell Blvd to Northern Blvd, our members are neighbors, coworkers, students and parents. Two gyms, one community." },
];

export default function About() {
  const { openJoin } = useSite();
  usePageMeta("About Iron Pump Gym | Bayside & Flushing Queens Community Gym", "The story of Iron Pump Gym, a no-nonsense, beginner-friendly gym serving the Bayside and Flushing, Queens community.");
  return (
    <>
      <PageHero eyebrow="About" title={<>Iron Pump<br /><span className="text-gold-grad">Queens made.</span></>} image="/images/athlete2.webp" />
      <section className="mx-auto grid max-w-7xl items-center gap-14 px-4 py-20 sm:px-8 lg:grid-cols-2" data-testid="about-story">
        <Reveal><img src="/images/athlete1.webp" alt="Member lifting at Iron Pump" loading="lazy" className="aspect-[4/5] w-full rounded-md object-cover" /></Reveal>
        <div className="space-y-6">
          <SectionHeading eyebrow="Our story" title="Started with one gym and a simple idea." />
          <Reveal delay={0.1} className="space-y-4 text-sm leading-relaxed text-white/65 sm:text-base">
            <p>Iron Pump started in Bayside with a simple idea: build the kind of gym we wanted to train in. Real equipment, a clean floor, and doors that stay open long after other gyms close.</p>
            <p>As the community grew, so did we. Opening our Flushing location meant members could train wherever life took them that day, and that's where "2 Gyms. 1 Membership." was born.</p>
            <p>Today, Iron Pump is home to first-timers, competitive lifters and everyone in between. We keep it simple: show up, put in the work, and help the person next to you.</p>
          </Reveal>
        </div>
      </section>
      <section className="border-y border-white/10 bg-[#0E0E0E]">
        <div className="mx-auto grid max-w-7xl gap-px bg-white/10 md:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1} className="bg-[#0E0E0E] p-8 sm:p-10">
              <div data-testid={`about-value-${i}`}>
                <v.icon className="h-9 w-9 text-[var(--gold)]" />
                <h3 className="h-display mt-5 text-3xl text-white">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-20 text-left sm:px-8">
        <Reveal className="flex flex-col items-start gap-6">
          <p className="h-display text-5xl text-white sm:text-7xl">Come train with us.</p>
          <button onClick={() => openJoin({ type: "guest_pass" })} className="btn-gold" data-testid="about-guest-pass-btn">Get a Guest Pass</button>
        </Reveal>
      </section>
    </>
  );
}
